import React, { useState, useRef, useEffect } from "react";
import { Book, Cpu, Flame, Quote, Square, Volume2 } from "lucide-react";
import { inventors, Inventor, projects, Project } from "@/data/projectsData";
import { toast } from "sonner";

interface GreatMindsExplorerProps {
  onSelectProject: (projectId: string) => void;
  inventorsList?: Inventor[];
  projectsList?: Project[];
}

export const GreatMindsExplorer = ({ onSelectProject, inventorsList, projectsList }: GreatMindsExplorerProps) => {
  const listInventors = Array.isArray(inventorsList) && inventorsList.length > 0 ? inventorsList : (Array.isArray(inventors) ? inventors : []);
  const listProjects = Array.isArray(projectsList) && projectsList.length > 0 ? projectsList : (Array.isArray(projects) ? projects : []);

  const [activeMindId, setActiveMindId] = useState<string>(listInventors[0]?.id || "tesla");
  const fallbackMind: Inventor = {
    id: "tesla",
    name: "Nikola Tesla",
    years: "1856 - 1943",
    field: "Electromagnetismo",
    quote: "El presente es de ellos; el futuro, por el cual realmente he trabajado, es mío.",
    description: "Pionero del electromagnetismo, la corriente alterna polifásica y las telecomunicaciones inalámbricas.",
    sketches: ["Bobina resonante de alta frecuencia"],
    notebookText: "La resonancia armónica del espacio y la bobina de chispa oscilante revelan el transporte de energía sin hilos.",
    replicatedProjects: ["tesla-motor"]
  };
  const activeMind = listInventors.find((inv) => inv?.id === activeMindId) || listInventors[0] || fallbackMind;

  const [isPlaying, setIsPlaying] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const handlePlayVoice = () => {
    if (!window.speechSynthesis) {
      toast.error("Tu navegador no soporta síntesis de voz.");
      return;
    }
    if (isPlaying) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      return;
    }

    window.speechSynthesis.cancel();
    const text = activeMind.notebookText;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "es-ES";

    // Query available voices to find a premium neural voice for Spanish
    const voices = window.speechSynthesis.getVoices();
    const spanishVoices = voices.filter((v) => v.lang.toLowerCase().startsWith("es"));
    
    // Prioritize natural or neural voices
    const selectedVoice = spanishVoices.find((v) => v.name.toLowerCase().includes("google") || v.name.toLowerCase().includes("natural"))
      || spanishVoices.find((v) => v.name.toLowerCase().includes("microsoft") || v.name.toLowerCase().includes("helena"))
      || spanishVoices.find((v) => v.name.toLowerCase().includes("samantha") || v.name.toLowerCase().includes("monica"))
      || spanishVoices.find((v) => v.lang.toLowerCase() === "es-es")
      || spanishVoices[0];

    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    // Set rates for maximum natural fluidity (rates around 0.95 - 1.05)
    if (activeMind.id === "da-vinci") {
      utterance.pitch = 0.9;
      utterance.rate = 0.95; // Wise, steady but fluid
    } else if (activeMind.id === "tesla") {
      utterance.pitch = 1.05;
      utterance.rate = 1.02; // Crisper, energetic and clear
    } else if (activeMind.id === "curie") {
      utterance.pitch = 1.0;
      utterance.rate = 1.0; // Clear and natural
    } else {
      utterance.pitch = 1.0;
      utterance.rate = 1.0;
    }

    utterance.onend = () => setIsPlaying(false);
    utterance.onerror = () => setIsPlaying(false);

    utteranceRef.current = utterance;
    setIsPlaying(true);
    window.speechSynthesis.speak(utterance);
  };

  useEffect(() => {
    if (isPlaying && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
    }
  }, [activeMindId]);

  return (
    <section id="mentes" className="relative py-24 border-t border-border bg-void">
      <div className="absolute inset-0 grid-blueprint opacity-10" />
      <div className="container relative">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono-tech text-[10px] text-primary tracking-widest uppercase">Cuadernos de Historia</span>
              <span className="h-px w-6 bg-primary/60" />
            </div>
            <h2 className="font-serif-display text-4xl md:text-6xl font-light">
              Los Cuadernos de <span className="text-ember italic">Prometeo</span>
            </h2>
            <p className="text-xs text-muted-foreground mt-2 max-w-xl leading-relaxed">
              Explora las notas de laboratorio, fórmulas primordiales y bocetos de los inventores que se atrevieron a robar el fuego. Haz clic en sus proyectos para llevarlos a la mesa de trabajo.
            </p>
          </div>

          {/* Inventors Tabs */}
          <div className="flex flex-wrap gap-2">
            {listInventors.map((inv) => (
              <button
                key={inv.id}
                onClick={() => setActiveMindId(inv.id)}
                className={`px-3 py-2 text-xs font-mono-tech border uppercase transition-colors duration-300 ${
                  activeMindId === inv.id
                    ? "bg-primary border-primary text-primary-foreground font-bold"
                    : "bg-card border-border hover:border-muted-foreground/60 text-muted-foreground hover:text-foreground"
                }`}
              >
                {inv.name}
              </button>
            ))}
          </div>
        </div>

        {/* Explorer Layout */}
        <div className="grid lg:grid-cols-12 gap-8 items-stretch">
          {/* Left panel: Biography and description */}
          <div className="lg:col-span-5 flex flex-col justify-between bg-card/30 backdrop-blur-sm border border-border p-6 md:p-8 rounded-lg">
            <div className="space-y-6">
              <div className="flex items-center justify-between border-b border-border/50 pb-4">
                <div>
                  <span className="font-mono-tech text-[9px] uppercase tracking-widest text-primary">
                    {activeMind.years}
                  </span>
                  <h3 className="font-serif-display text-3xl mt-1 text-foreground">
                    {activeMind.name}
                  </h3>
                </div>
                <span className="px-2.5 py-0.5 font-mono-tech text-[9px] border border-primary/20 text-primary-glow uppercase">
                  {activeMind.field}
                </span>
              </div>

              <blockquote className="font-serif-display text-lg italic text-foreground/90 border-l-2 border-primary pl-4 leading-relaxed my-4">
                <Quote className="w-4 h-4 text-primary mb-1 opacity-60 inline mr-1" />
                "{activeMind.quote}"
              </blockquote>

              <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                {activeMind.description}
              </p>
            </div>

            {/* List of related projects */}
            <div className="pt-8 mt-8 border-t border-border/50 space-y-4">
              <h4 className="font-mono-tech text-[10px] uppercase text-muted-foreground tracking-wider">
                Proyectos Para Rehacer:
              </h4>
              <div className="space-y-2">
                {(activeMind?.replicatedProjects || []).map((projId) => {
                  const proj = listProjects.find((p) => p.id === projId);
                  if (!proj) return null;
                  return (
                    <button
                      key={projId}
                      onClick={() => onSelectProject(projId)}
                      className="w-full flex items-center justify-between p-3 border border-border hover:border-primary bg-card/65 transition-all text-left group"
                    >
                      <div>
                        <h5 className="font-serif-display text-base text-foreground group-hover:text-ember transition-colors">
                          {proj.title}
                        </h5>
                        <span className="font-mono-tech text-[8px] text-muted-foreground uppercase tracking-widest">
                          {proj.discipline} · {proj.difficulty}
                        </span>
                      </div>
                      <Cpu className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right panel: Historical manuscript notebook log */}
          <div className="lg:col-span-7 border border-primary/20 bg-void/50 rounded-lg p-6 md:p-8 flex flex-col justify-between relative overflow-hidden">
            {/* Blueprint grid background */}
            <div className="absolute inset-0 grid-blueprint opacity-5 pointer-events-none" />

            {/* Notebook frame styling */}
            <div className="absolute top-4 left-4 w-4 h-4 border-l border-t border-primary/20" />
            <div className="absolute top-4 right-4 w-4 h-4 border-r border-t border-primary/20" />
            <div className="absolute bottom-4 left-4 w-4 h-4 border-l border-b border-primary/20" />
            <div className="absolute bottom-4 right-4 w-4 h-4 border-r border-b border-primary/20" />

            <div className="space-y-6 relative z-10">
              <div className="flex items-center justify-between border-b border-border/20 pb-2">
                <div className="flex items-center gap-2 font-mono-tech text-[10px] text-primary">
                  <Book className="w-3.5 h-3.5" />
                  <span>TRANSCRIPCIÓN DE CUADERNO ORIGINAL</span>
                </div>
                <div className="flex items-center gap-2">
                  {isPlaying && (
                    <div className="flex items-end gap-0.5 h-3 px-2">
                      <span className="w-0.5 bg-primary animate-[pulse_1s_infinite_0s]" style={{ height: '60%' }} />
                      <span className="w-0.5 bg-primary animate-[pulse_1s_infinite_0.2s]" style={{ height: '100%' }} />
                      <span className="w-0.5 bg-primary animate-[pulse_1s_infinite_0.4s]" style={{ height: '40%' }} />
                      <span className="w-0.5 bg-primary animate-[pulse_1s_infinite_0.1s]" style={{ height: '80%' }} />
                    </div>
                  )}
                  <button
                    onClick={handlePlayVoice}
                    className="flex items-center gap-1.5 px-3 py-1 border border-primary/30 hover:border-primary bg-primary/10 hover:bg-primary/20 text-[9px] font-mono-tech text-foreground uppercase transition-all rounded cursor-pointer"
                    title={isPlaying ? "Detener Lectura" : "Escuchar Reflexión"}
                  >
                    {isPlaying ? <Square className="w-2.5 h-2.5 text-ember" /> : <Volume2 className="w-2.5 h-2.5 text-primary-glow" />}
                    <span>{isPlaying ? "Detener" : "Escuchar"}</span>
                  </button>

                  <button
                    onClick={() => {
                      const event = new CustomEvent("start-prometheus-chat", {
                        detail: {
                          characterId: activeMind.id,
                          initialMessage: `Hola ${activeMind.name}. He estado leyendo tus notas sobre "${activeMind.notebookText.slice(0, 45)}...". ¿Cómo aplicarías tus principios científicos para un taller o casa autosuficiente en Puerto Rico?`
                        }
                      });
                      window.dispatchEvent(event);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1 border border-[#00F5FF]/30 hover:border-[#00F5FF] bg-[#00F5FF]/10 hover:bg-[#00F5FF]/20 text-[9px] font-mono-tech text-[#00F5FF] uppercase transition-all rounded cursor-pointer"
                  >
                    <Quote className="w-2.5 h-2.5" />
                    <span>Discutir con {activeMind.name.split(" ")[0]}</span>
                  </button>
                </div>
              </div>

              <div className="font-serif-display text-lg md:text-xl text-foreground/80 leading-relaxed italic border-l-2 border-primary/40 pl-6 my-4 bg-primary/5 py-4 pr-4 rounded-r">
                "{activeMind.notebookText}"
              </div>

              {/* Sketches and designs preview */}
              <div className="space-y-4 pt-4">
                <h4 className="font-mono-tech text-[10px] uppercase text-muted-foreground tracking-wider border-b border-border/40 pb-1">
                  Bocetos y Diagramas Técnicos
                </h4>
                <div className="grid sm:grid-cols-2 gap-4">
                  {activeMind.sketches.map((sk, idx) => (
                    <div
                      key={idx}
                      className="bg-card/45 border border-border p-4 rounded hover:border-primary/40 transition-colors cursor-default"
                    >
                      <span className="font-mono-tech text-[8px] text-primary block">
                        SKETCH-0{idx + 1} // ARCHIVE
                      </span>
                      <p className="font-serif-display text-sm text-foreground mt-2">{sk}</p>
                      <span className="block text-[9px] text-muted-foreground/60 mt-1 font-mono-tech">
                        Fuego de las ideas · Rediseñado en 3D
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8 border-t border-border/40 pt-4 flex items-center justify-between font-mono-tech text-[8px] text-muted-foreground/50">
              <span>PROMETHEUS ARCHIVES // INVENTORES</span>
              <span>PATENT DATA LOADED</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
